import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Turnstile, turnstileActive } from '../components/Turnstile';
import { SaasError, saasPasswordResetRequest } from '../lib/saas-client';

export function ForgotPasswordPanel() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (user) return <Navigate to="/account" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await saasPasswordResetRequest(email.trim(), token || undefined);
      setSubmitted(true);
    } catch (err) {
      // Turnstile tokens are single-use; reissue a fresh one for the retry.
      setToken('');
      setCaptchaKey((k) => k + 1);
      setError(err instanceof SaasError ? err.message : 'Request failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="panel auth-panel">
        <header className="page-header">
          <h1>Forgot your password?</h1>
          <p>Enter your email and we'll send you a one-time reset link. It expires in an hour.</p>
        </header>

        {submitted ? (
          <div className="form-success">
            <p>
              <strong>If an account exists for {email || 'that email'}, a reset link
              is on its way.</strong>
            </p>
            <p className="muted">
              Check your inbox (and spam folder) for an email from Willow. The link
              expires in one hour and can only be used once.
            </p>
            <p style={{ marginTop: '1rem' }}>
              <Link to="/login">← Back to sign in</Link>
            </p>
          </div>
        ) : (
          <>
            <form onSubmit={onSubmit} className="auth-form">
              <label>
                <span>Email</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <Turnstile key={captchaKey} onVerify={setToken} />
              {error && <p className="form-error">{error}</p>}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || (turnstileActive && !token)}
              >
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
            <p className="auth-switch">
              <Link to="/login">← Back to sign in</Link>
            </p>
          </>
        )}
      </section>
    </div>
  );
}
